'use client';

import { API_ROUTES, HTTP_METHODS, useApiMutation } from '@shared/api-utils';
import { Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@shared/components/shadcn';
import { Spinner } from '@shared/icons';
import { Verification } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { Link } from '../../../Link/Link';
import { useGlobalDialogPolling } from '../../hooks/useGlobalDialogPolling';

/**
 * Displays and controls the Affordability Check dialog.
 *
 * Behaviour overview:
 * - Listens to shared long polling for global popup messages via useGlobalDialogPolling hook.
 * - Opens the dialog only when the server signals an `AFFORDABILITY_MIC_POPUP` message.
 * - Registers its open state with the polling provider to pause polling when open (prevents race conditions).
 * - Caches the popup `id` in a ref the moment the dialog opens, so action requests can include it even after
 *   the long poll resets its data on dialog close.
 * - Sends the required payload to the affordability endpoint in the shape `{ popupValue, id }` when the
 *   user clicks either primary or secondary CTA.
 * - Long polling is always enabled in dev environment, controlled by ?popup=POPUP_TYPE parameter.
 *
 * WHY these choices:
 * - Using shared polling hook eliminates duplicate API calls (one poll for all dialogs).
 * - Registering open state with provider pauses all polling when any dialog is open.
 * - Using a ref to store `id` ensures we always send the correct identifier after the dialog closes.
 * - Interaction outside the modal and Escape key are prevented because the dialog requires explicit user action.
 * - Popup type parameter controls which dialog appears in dev environment.
 */
function AffordabilityCheckDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations('global_dialogs.affordability_check_dialog');
  const tForms = useTranslations('form');
  const latestDialogIdRef = useRef<string>('');

  const POPUP_TYPE = 'AFFORDABILITY_MIC_POPUP';

  // Use shared polling hook - registers this dialog's open state and receives polling data
  const { data } = useGlobalDialogPolling(POPUP_TYPE, isOpen);

  const affordabilityMutation = useApiMutation<void, { popupValue: string; id: string }>(API_ROUTES.PLAYER_KYC.AFFORDABILITY.CHECK, HTTP_METHODS.POST);
  const isLoading = affordabilityMutation.isPending;

  useEffect(() => {
    if (data?.messageType === POPUP_TYPE && !isOpen && data.id?.trim()) {
      latestDialogIdRef.current = data.id;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsOpen(true);
    }
  }, [data?.messageType, data?.id, isOpen]);

  // Handles dialog closing - open prop must be used with onOpenChange to control state explicitly
  const handleOpenChange = (openState: boolean) => {
    if (!openState) {
      setIsOpen(false);
    }
  };

  // Handles continue button click - submits CONTINUE action
  const handleContinue = () => {
    affordabilityMutation.mutate({ popupValue: 'CONTINUE', id: latestDialogIdRef.current });
    setIsOpen(false);
  };

  // Handles safer gambling button click - submits SAFER_GAMBLING action
  const handleSaferGambling = () => {
    affordabilityMutation.mutate({ popupValue: 'SAFER_GAMBLING', id: latestDialogIdRef.current });
    setIsOpen(false);
  };

  // Do not render anything unless the dialog is actually open (aligns with questionnaire display logic guards)
  if (!isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="affordability-check-dialog"
        // Prevent dismiss to force an explicit choice by the user
        onInteractOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
        data-track-feature="My Account"
        data-track-container="Affordability Check"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 flex-col gap-2 py-2">
          {/* Icon */}
          <Verification size="extra-extra-large" data-testid="affordability-check-icon" />

          <h4 className="mt-3" data-testid="affordability-check-heading">
            {t('heading')}
          </h4>

          <DialogDescription asChild>
            <div className="space-y-4">
              <p data-testid="affordability-check-message-intro">{t('message_intro')}</p>
              <p data-testid="affordability-check-message-body">{t('message_body')}</p>
            </div>
          </DialogDescription>
        </div>

        <DialogFooter data-track-container="Actions">
          <DialogClose asChild>
            <Button disabled={isLoading} variant="secondary" asChild onClick={handleSaferGambling} data-testid="safer-gambling-button" data-track-name="Safer Gambling">
              <Link href={t('safer_gambling_link')} target="_blank" className="underline">
                {t('secondary_cta')}
              </Link>
            </Button>
          </DialogClose>

          <DialogClose asChild>
            <Button onClick={handleContinue} data-testid="continue-button" data-track-name="Continue" disabled={isLoading}>
              {isLoading ? <Spinner size="small" className="animate-spin w-5" aria-label={tForms('aria.loading')} /> : t('primary_cta')}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { AffordabilityCheckDialog };
